import { matchPath } from "react-router-dom";

import { tenantLabel } from "./tenantLabels";

export interface TenantTitleContext {
  settings?: {
    labels?: Record<string, string | null | undefined>;
  };
}

type TenantRouteTitle = (tenant: TenantTitleContext | null) => string;
type PageTitleRoute = readonly [string, string | TenantRouteTitle];

const tenantRouteTitle =
  (key: string, fallback: string): TenantRouteTitle =>
  (tenant) =>
    tenantLabel(tenant, key, fallback);

const PAGE_TITLE_ROUTES: PageTitleRoute[] = [
  ["/accept-invitation", "Accept Invitation"],
  ["/forgot-password", "Forgot Password"],
  ["/reset-password", "Reset Password"],
  ["/login", "Login"],
  ["/my-account", "My Account"],
  ["/account/company-settings", "Company Settings"],
  ["/dashboard", "Dashboard"],
  ["/export-label", "Export Label"],
  ["/audit-histories/:entityType", "Audit History"],
  ["/audit/:entityType/:entityId", "Audit Details"],
  ["/supplier-items", "Supplier Items"],
  ["/brands", "Brands"],
  ["/locations/:locationId", "Location Details"],
  ["/locations", tenantRouteTitle("locations", "Locations")],
  ["/customers", tenantRouteTitle("customers", "Customers")],
  ["/price-rules", "Price Rules"],
  ["/certification-types", "Certification Types"],
  ["/cost-calculation", "Cost Calculation"],
  ["/suppliers", tenantRouteTitle("suppliers", "Suppliers")],
  ["/product-allergens", "Allergens"],
  ["/product-claims", "Claims"],
  ["/product-types", "Product Types"],
  ["/production-costs", "Production Costs"],
  ["/bom-items", tenantRouteTitle("assembly_items", "BOM Items")],
  ["/my-products/new", "Add Product"],
  ["/my-products/:productId", "Product Details"],
  ["/my-products", "Products"],
  ["/preservatives", "Preservatives"],
  ["/manufactured-products-employee", "Manufacture New"],
  ["/purchase-orders/:purchaseOrderId/receive", "Receive Purchase Order"],
  ["/purchase-orders/new", "Create Purchase Order"],
  ["/purchase-orders/:purchaseOrderId", "Purchase Order Details"],
  ["/purchase-orders", "Purchase Orders"],
  ["/receiving-index", tenantRouteTitle("receiving", "Receiving")],
  ["/receiving-invoices/new", "Add Receiving Invoice"],
  ["/receiving-invoices/:invoiceId", "Receiving Invoice Details"],
  ["/receiving-invoices", "Receiving Invoices"],
  ["/manufacture-product", "New Production"],
  ["/manufactured-products/:productId", "Production Details"],
  [
    "/manufactured-products",
    tenantRouteTitle("manufactured_products", "Manufactured"),
  ],
  ["/calendar/production", "Production Calendar"],
  ["/packaging-new", "Package New"],
  ["/packaging-reports", tenantRouteTitle("packaging", "Packaging")],
  ["/unit-of-measures", "Units of Measure"],
  ["/tax-rates", "Tax Rates"],
  ["/barcode-scanner", "Barcode Extractor"],
  ["/qa-reports", "QA Reports"],
  ["/media-manager", "Media Manager"],
  ["/laboratories", "Laboratories"],
  ["/equipments", "Equipment"],
  ["/visitor-types", "Visitor Types"],
  ["/lab-samples/new", "New Lab Sample"],
  ["/lab-samples/:id", "Lab Sample Details"],
  ["/lab-samples", "Lab Samples"],
  ["/equipment-maintenance/new", "New Equipment Maintenance"],
  ["/equipment-maintenance/:id", "Equipment Maintenance Details"],
  ["/equipment-maintenance", "Equipment Maintenance"],
  ["/visitors/new", "New Visitor"],
  ["/visitors/:id", "Visitor Details"],
  ["/visitors", "Visitors"],
  ["/process-statuses", "Process Statuses"],
  ["/in-process-monitoring", "In-process Monitoring"],
  ["/create-recipe", "Create Recipe"],
  ["/recipes/:recipeId", "Recipe Details"],
  ["/recipes", "Recipes"],
  ["/quickbooks/callback", "QuickBooks"],
  ["/quickbooks-login", "QuickBooks"],
  ["/inventory-raw-material", "Raw Material Inventory"],
  [
    "/inventory-manufactured-material",
    tenantRouteTitle("manufactured_products", "Manufactured Inventory"),
  ],
  ["/pallet-let-down", "Pallet Let Down"],
  ["/create-order", "Create Order"],
  ["/orders/:orderId", "Order Details"],
  ["/orders", tenantRouteTitle("orders", "Orders")],
  ["/calendar/orders", "Orders Calendar"],
  ["/import-skus", "Import SKUs"],
  ["/shippings/:shippingId", "Shipment Details"],
  ["/shippings", tenantRouteTitle("shipping", "Shipments")],
  ["/credit-returns/:creditReturnId", "Return & Credit Details"],
  ["/credit-returns", "Returns & Credits"],
  ["/traceability/manufactured", "Manufactured Traceability"],
  ["/traceability/supplier", "Supplier Traceability"],
  ["/traceability/packaging-barcode", "Packaging Barcode Lookup"],
  ["/traceability/receiving-item", "Receiving Item Lookup"],
  ["/traceability", "Traceability"],
  ["/users", "Users"],
];

export function pageTitleForPath(
  pathname: string,
  tenant: TenantTitleContext | null = null,
): string | null {
  const route = PAGE_TITLE_ROUTES.find(([path]) =>
    matchPath({ path, end: true }, pathname),
  );

  if (!route) {
    return null;
  }

  const title = route[1];
  return typeof title === "function" ? title(tenant) : title;
}
